import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QrReader from 'react-qr-scanner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import successSound from '../../sound/scan.mp3';
import failSound from '../../sound/fail.mp3';
import '../../styles/QRScanner.css';
import { checkTicket, checkTicketThinkSoft} from '../../services/authService';
import Popup from '../Popup/Popup';

function QRScanner() {
  const navigate = useNavigate();
  const [audio] = useState(new Audio(successSound));
  const [failAudio] = useState(new Audio(failSound));
  const [lastScanTime, setLastScanTime] = useState(0);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [torch, setTorch] = useState(false);
  const [stream, setStream] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingCode, setProcessingCode] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [ticketInfo, setTicketInfo] = useState(null);
  const [key, setKey] = useState(0);
  const [torchSupported, setTorchSupported] = useState(false);

  useEffect(() => {
    return () => {
      audio.pause();
      audio.currentTime = 0;
      failAudio.pause();
      failAudio.currentTime = 0;
    };
  }, [audio, failAudio]);

  useEffect(() => {
    let currentStream = null;
    
    navigator.mediaDevices.getUserMedia({
      video: { 
        facingMode: { exact: "environment" }
      }
    })
    .then(mediaStream => {
      console.log('Camera stream started');
      currentStream = mediaStream;
      setStream(mediaStream);
    })
    .catch(err => {
      console.error('Không thể truy cập camera:', err);
    });

    return () => {
      console.log('Cleanup camera stream');
      if (currentStream) {
        currentStream.getTracks().forEach(track => {
          track.stop();
          console.log('Track stopped:', track.label);
        });
      }
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
          console.log('Track stopped:', track.label);
        });
      }
    };
  }, []);

  useEffect(() => {
    const checkTorchSupport = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: { exact: "environment" }
          }
        });
        
        const track = stream.getVideoTracks()[0];
        const capabilities = track.getCapabilities();
        
        setTorchSupported(capabilities?.torch || false);
        
        stream.getTracks().forEach(track => track.stop());
      } catch (err) {
        console.error('Lỗi kiểm tra đèn flash:', err);
        setTorchSupported(false);
      }
    };
    
    checkTorchSupport();
  }, []);

  const playSuccessSound = () => {
    audio.currentTime = 0;
    audio.play().catch(error => {
      console.log('Không thể phát âm thanh:', error);
    });
  };
  
  const playFailSound = () => {
    failAudio.currentTime = 0;
    failAudio.play().catch(error => {
      console.log('Không thể phát âm thanh:', error);
    });
  };

  const handleScan = async (data) => {
    console.log('handleScan được gọi', { data, isProcessing });

    if (!data || !data.text || ticketInfo != null || showSuccess || showError) {
      return;
    }

    if (!isProcessing) {
      const code = data.text;
      const currentTime = Date.now();

      try {
        setIsProcessing(true);
        setProcessingCode(code);
        setLastScanTime(currentTime);

        // const response = await checkTicket(code);

        const response = await checkTicketThinkSoft(code);
        
        if (response.success) {
          playSuccessSound();
          setTicketInfo(response.data);
          setShowSuccess(true);
        } else {
          playFailSound();
          setErrorMessage(response.message || 'Vé không hợp lệ');
          setShowError(true);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsProcessing(false);
        setProcessingCode(null);
      }
    }
  };

  const handleError = (err) => {
    console.error('QR Scanner error:', err);
    if (isProcessing) return;
    
    setIsProcessing(false);
    setProcessingCode(null);
    setErrorMessage(err.message || 'Có lỗi xảy ra khi quét QR');
    setShowError(true);
    
    setTimeout(() => {
      setShowError(false);
      setErrorMessage('');
    }, 1500);
  };

  const handleClose = () => {
    navigate('/daily-tickets');
  };

  const toggleTorch = async () => {
    if (stream) {
      const track = stream.getVideoTracks()[0];
      
      try {
        setKey(prevKey => prevKey + 1);
        
        try {
          await track.applyConstraints({
            advanced: [{ torch: !torch }]
          });
          setTorch(!torch);
        } catch (err) {
          try {
            await track.applyConstraints({
              advanced: [{ fillLightMode: !torch ? 'torch' : 'none' }]
            });
            setTorch(!torch);
          } catch (err2) {
            console.error('Không thể bật đèn flash:', err2);
            setShowError(true);
            setErrorMessage('Thiết bị không hỗ trợ đèn flash');
          }
        }
        
      } catch (err) {
        console.error('Lỗi bật/tắt đèn flash:', err);
        setShowError(true);
        setErrorMessage('Không thể bật/tắt đèn flash');
      }
    }
  };

  const renderTicketInfo = (ticket) => (
    <div className="ticket-info">
      <p><h3>Thông tin vé</h3></p>
      <p><strong>Mã:</strong> {ticket.code}</p>
      <p><strong>Tên:</strong> {ticket.name}</p>
      <p><strong>Trạng thái:</strong> {ticket.status}</p>
      <p><strong>Check-in lúc:</strong> {new Date(ticket.checkin_at).toLocaleString()}</p>
      {ticket.status === 'CHECKOUT' && <p><strong>Check-out lúc:</strong> {new Date(ticket.checkout_at).toLocaleString()}</p>}
      {ticket.commission > 0 && <p><strong>Hoa hồng:</strong> {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(ticket.commission)}</p>}
    </div>
  );

  return (
    <>
      <div className="qr-scanner-container">
        <button className="close-button" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="scanner-overlay">
          <div className="scanner-frame"></div>
          <p className="scanner-text">Đặt mã QR vào khung hình</p>
        </div>

        <QrReader
          key={key}
          onError={(err) => handleError(err)}
          onScan={(data) => handleScan(data)}
          constraints={{
            video: { 
              facingMode: { exact: "environment" },
              torch: torch
            }
          }}
        />

        {torchSupported && (
          <button className={`torch-button ${torch ? 'active' : ''}`} onClick={toggleTorch}>
            <FontAwesomeIcon icon={faLightbulb} size="lg" />
          </button>
        )}

        {isProcessing && processingCode && (
          <div className="processing-overlay">
            <div className="processing-content">
              <div className="processing-spinner" />
              <div className="processing-text">
                Đang xử lý mã: {processingCode}
              </div>
            </div>
          </div>
        )}
      </div>

      {showError && (
        <Popup 
          type="error"
          message={errorMessage}
          onClose={() => {
            setShowError(false);
            handleClose();
          }}
        />
      )}

      {showSuccess && ticketInfo && (
        <Popup 
          type="message"
          reactElement={renderTicketInfo(ticketInfo)}
          onClose={() => {
            setShowSuccess(false);
            handleClose();
          }}
        />
      )}
    </>
  );
}

export default QRScanner; 